<template>
    <div class="m-2 p-0 app-content d-flex flex-column">
        <!-- Header -->
        <header class="d-flex align-items-center bg-internal-comments text-white round mb-1">
            <div class="saysimple-icon comments mr-2 font-large-1" />
            <span class="m-0 text-white flex-fill">
                <strong>{{ $t("entities.internal-comments.Internal comments") }}</strong>
            </span>
        </header>

        <div id="internal-comments" class="flex-grow-1 round position-relative">
            <div
                v-if="internalCommentsFormatted.length === 0"
                class="d-flex flex-column justify-content-center align-items-center h-100"
            >
                <img class="mb-1" height="110" src="~@/assets/images/internal-comments-zero-state.svg" />
                <small class="text-secondary">{{ $t("entities.internal-comments.No internal comments") }}</small>
            </div>

            <template v-for="comment in internalCommentsFormatted">
                <div :class="{ active: internalCommentId === comment.id }" class="internal-comment">
                    <div v-if="comment.type === 'event'" class="divider text-secondary round-extra-large mx-auto my-1">
                        {{
                            formatDate(comment.date, {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                            })
                        }}
                    </div>

                    <b-card
                        v-else
                        :key="comment.id"
                        :class="{ 'mt-1': comment.otherUser === true }"
                        class="mx-2 mb-25 shadow-none"
                    >
                        <div v-if="getUserData().id === comment.userId" class="dropdown">
                            <b-dropdown no-caret right toggle-class="p-0" variant="link">
                                <template #button-content>
                                    <font-awesome-icon
                                        :icon="['fa', 'ellipsis-h']"
                                        class="align-middle text-body mx-auto"
                                        size="1x"
                                    />
                                </template>
                                <b-dropdown-item v-if="$can('update', 'internalComments')" @click="prefill(comment)">
                                    <font-awesome-icon :icon="['fa', 'edit']" />
                                    <span class="ml-75">{{ $t("Edit") }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item v-if="$can('delete', 'internalComments')" @click="remove(comment)">
                                    <font-awesome-icon :icon="['fa', 'trash']" />
                                    <span class="ml-75">{{ $t("Delete") }}</span>
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>

                        <b-card-title>{{ getUserName(comment) }}</b-card-title>

                        <!-- Show comment -->
                        <template v-if="internalCommentId !== comment.id">
                            <b-card-text class="pb-1">{{ comment.comment }}</b-card-text>
                            <b-card-text v-if="internalCommentId !== comment.id" class="text-muted time">
                                {{
                                    formatDate(comment.createdAt, {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })
                                }}
                                {{ comment.editedAt ? ` - ${$t("updated-at")} ${formatDate(comment.editedAt)}` : "" }}
                            </b-card-text>
                        </template>
                        <!-- Inline edit comment -->
                        <template v-else>
                            <b-form-textarea
                                v-model="internalCommentForm.comment"
                                :placeholder="comment.comment"
                                class="edit-textarea-internal-comment mb-1 bg-white border-0"
                                no-resize
                                required
                                rows="2"
                            />
                            <div class="actions">
                                <b-button class="mr-50" variant="light" @click="reset">
                                    {{ $t("Cancel") }}
                                </b-button>
                                <b-button class="round-medium" variant="primary" @click="save(false)">
                                    {{ $t("Edit") }}
                                </b-button>
                            </div>
                        </template>
                    </b-card>
                </div>
            </template>
        </div>

        <validation-observer ref="simpleRules" class="internal-comments-compose w-100 px-2">
            <b-form
                v-if="$can('create', 'internalComments') || $can('update', 'internalComments')"
                class="form mx-75 position-relative"
            >
                <b-form-textarea
                    id="textarea-internal-comment"
                    v-model="internalCommentFormNew.comment"
                    :placeholder="$t('entities.internal-comments.Add new comment')"
                    class="bg-white shadow"
                    no-resize
                    required
                    rows="2"
                />

                <div class="actions">
                    <b-button
                        :disabled="isLoadingInternalComments"
                        class="round-medium"
                        variant="primary"
                        @click="save(true)"
                    >
                        <b-spinner v-if="isLoadingInternalComments" small />
                        <font-awesome-icon v-else :icon="['fa', 'paper-plane-top']" size="sm" />
                    </b-button>
                </div>
            </b-form>
        </validation-observer>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import useActiveChat from "@/composables/useActiveChat";
import useUsers from "@/composables/useUsers";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { ChatLogEntryTypeType } from "@/types/chatLogEntryType.type";
import { formatDate } from "@/utils/luxon";
import { required } from "@validations";
import { ref } from "@vue/composition-api";
import { defineComponent } from "@vue/composition-api/dist/vue-composition-api";
import {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardText,
    BCardTitle,
    BDropdown,
    BDropdownItem,
    BForm,
    BFormTextarea,
    BSpinner,
} from "bootstrap-vue";
import * as lodash from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";

export default defineComponent({
    components: {
        BAlert,
        BAvatar,
        BButton,
        BCard,
        BCardBody,
        BCardHeader,
        BCardText,
        BCardTitle,
        BDropdown,
        BDropdownItem,
        BForm,
        BFormTextarea,
        BSpinner,
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        internalComments: {
            type: Array,
            required: false,
        },
        isLoadingInternalComments: {
            type: Boolean,
            required: true,
        },
        identityId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            required,
        };
    },
    methods: {
        save(isNewComment) {
            // Save new internal comment
            if (isNewComment) {
                if (this.internalCommentFormNew.comment.length > 0) {
                    this.$emit("create-internal-comment", {
                        userId: getUserData().id,
                        identityId: this.identityId,
                        comment: this.internalCommentFormNew.comment,
                    });

                    this.resetFormNew();
                } else {
                    this.toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.internal-comments.Internal comment"),
                            text: i18n.t("entities.internal-comments.not-empty"),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                }

                return;
            }

            // Update existing comment
            if (this.internalCommentForm.comment.length === 0) {
                this.toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t("entities.internal-comments.Internal comment"),
                        text: i18n.t("entities.internal-comments.not-empty"),
                        icon: "times",
                        variant: "danger",
                    },
                });

                return;
            }

            this.$emit("update-internal-comment", {
                internalCommentId: this.internalCommentId,
                identityId: this.identityId,
                comment: this.internalCommentForm.comment,
            });
            this.updateActiveChatLogEntry(
                this.internalCommentId,
                ChatLogEntryTypeType.INTERNAL_COMMENT,
                this.internalCommentForm.createdAt,
                { comment: this.internalCommentForm.comment }
            );

            this.reset();
            this.$nextTick(() => this.$refs.simpleRules.reset());
        },
        getUserName(comment) {
            const { users } = useUsers();

            const user = users.value.find((user) => user.id === comment.userId);

            if (user) {
                return user.name;
            }
        },
    },
    computed: {
        internalCommentsFormatted() {
            const internalCommentsList = [];
            let currentDate = "";
            let currentUserId = 0;

            lodash.forEach(this.internalComments, (internalComment) => {
                if (currentUserId === 0) {
                    currentUserId = internalComment.userId;
                }

                let commentDate = new Date(internalComment.createdAt);
                commentDate = (
                    commentDate.getFullYear() +
                    "" +
                    (commentDate.getMonth() + 1) +
                    "" +
                    commentDate.getDate()
                ).substring(0, 10);

                if (commentDate !== currentDate) {
                    currentDate = commentDate;

                    internalCommentsList.push({
                        type: "event",
                        date: internalComment.createdAt,
                    });
                }

                // If we 'switch' users in the comments, we add an extra value for design purposes
                if (currentUserId !== internalComment.userId) {
                    internalComment.otherUser = true;
                    currentUserId = internalComment.userId;
                }

                internalCommentsList.push(internalComment);
            });

            return internalCommentsList;
        },
    },
    // @ts-ignore
    setup(props, context) {
        const toast = useToast();
        const internalCommentId = ref(null);
        const internalCommentForm = ref({
            comment: "",
            createdAt: new Date(),
        });
        const { updateActiveChatLogEntry, deleteActiveChatLogEntry, addActiveChatEntry } = useActiveChat();

        const prefill = (comment) => {
            internalCommentId.value = comment.id;
            internalCommentForm.value.comment = comment.comment;
            internalCommentForm.value.createdAt = new Date(comment.createdAt);
        };

        const reset = () => {
            internalCommentId.value = null;
            internalCommentForm.value = {
                comment: "",
                createdAt: new Date(),
            };
        };

        const remove = (comment) => {
            context.root.$bvModal
                .msgBoxConfirm(i18n.t("entities.internal-comments.confirm-delete.text"), {
                    title: i18n.t("entities.internal-comments.confirm-delete.title"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((value) => {
                    if (value === true) {
                        deleteActiveChatLogEntry(
                            comment.id,
                            ChatLogEntryTypeType.INTERNAL_COMMENT,
                            new Date(comment.createdAt)
                        );
                        context.emit("delete-internal-comment", {
                            internalCommentId: comment.id,
                            identityId: props.identityId,
                        });
                    }
                });
        };

        // For new comments we have to use another object because both options, new and edit, are possible at the same time
        const internalCommentFormNew = ref({
            comment: "",
        });

        const resetFormNew = () => {
            internalCommentFormNew.value = {
                comment: "",
            };
        };

        return {
            formatDate,
            internalCommentForm,
            internalCommentId,
            getUserData,

            prefill,
            reset,
            remove,

            internalCommentFormNew,
            resetFormNew,

            toast,

            updateActiveChatLogEntry,
            addActiveChatEntry,
        };
    },
});
</script>

<style lang="scss">
@import "~@/assets/scss/components/inbox/internalComments.scss";
</style>
